import React, { useState } from 'react';
import Breadcrumb from '../../components/breadcrumb';
import UQDataGrid, { UQDataGridColumn } from '../../components/data-grid';
import UQSpacer from '../../components/spacer';
import UQCode from '../../components/code';
import UQIcon from '../../components/icon';
import UQCard from '../../components/card';
import { BREADCRUMB_ROOT } from '../../common/config';

type ProgressCell = {
  label: string;
  status: 'completed' | 'overdue' | 'warning' | 'in-progress';
};

type Item = {
  id: string;
  project: string;
  customer: string;
  progress: ProgressCell;
  date: string;
  person: string;
};

const items: Item[] = [
  {
    id: '124564840',
    project: 'Sharepoint',
    customer: 'Lorem ipsum',
    progress: { label: 'Completed', status: 'completed' },
    date: '18.12.2024',
    person: 'Hans Hugetobler',
  },
  {
    id: '124564841',
    project: 'Sharepoint',
    customer: 'Lorem ipsum',
    progress: { label: 'Overdue', status: 'overdue' },
    date: '18.12.2024',
    person: 'Hans Hugetobler',
  },
  {
    id: '124564842',
    project: 'Sharepoint',
    customer: 'Lorem ipsum',
    progress: { label: 'Completed', status: 'completed' },
    date: '18.12.2024',
    person: 'Hans Hugetobler',
  },
  {
    id: '124564843',
    project: 'Sharepoint',
    customer: 'Lorem ipsum',
    progress: { label: 'Warning', status: 'warning' },
    date: '18.12.2024',
    person: 'Hans Hugetobler',
  },
  {
    id: '124564844',
    project: 'Sharepoint',
    customer: 'Lorem ipsum',
    progress: { label: 'In Progress', status: 'in-progress' },
    date: '18.12.2024',
    person: 'Hans Hugetobler',
  },
];

const attributes = [
  {
    attribute: 'items',
    type: 'T[]',
    default: '[]',
    description: 'The data to display in the grid.',
  },
  {
    attribute: 'columns',
    type: 'UQDataGridColumn<T>[]',
    default: '[]',
    description: 'The column definitions for the grid.',
  },
  {
    attribute: 'sortable',
    type: 'boolean',
    default: 'false',
    description: 'Enables sorting for columns with a compare function.',
  },
  {
    attribute: 'selectionMode',
    type: "'none' | 'single' | 'multiselect'",
    default: "'none'",
    description: 'Defines the row selection mode: none, single, or multiple.',
  },
  {
    attribute: 'getRowId',
    type: '(item: T) => string | number',
    default: 'undefined',
    description: 'Function to get the unique ID of a row.',
  },
  {
    attribute: 'onRowSelect',
    type: '(selectedRows: T[]) => void',
    default: 'undefined',
    description: 'Callback triggered when rows are selected.',
  },
  {
    attribute: 'onSort',
    type: '(column: UQDataGridColumn<T>) => void',
    default: 'undefined',
    description: 'Callback triggered when a column is sorted.',
  },
  {
    attribute: 'showCheckbox',
    type: 'boolean',
    default: 'false',
    description: 'Displays a checkbox column for row selection.',
  },
  {
    attribute: 'compactCheckboxCell',
    type: 'boolean',
    default: 'false',
    description: 'Removes padding from the checkbox cell for a more compact look.',
  },
  {
    attribute: 'rowLines',
    type: 'boolean',
    default: 'false',
    description: 'Shows horizontal lines between rows.',
  },
  {
    attribute: 'headerColor',
    type: 'string',
    default: 'undefined',
    description: 'Custom color for the header text.',
  },
  {
    attribute: 'minWidth',
    type: 'string',
    default: 'undefined',
    description: 'Minimum width for a column (set per column, e.g. "120px").',
  },
  {
    attribute: 'emptyMessage',
    type: 'string',
    default: 'undefined',
    description: 'Optional message to display when the grid has no items. If not set, the table body will be empty.',
  },
];

const UQDataGridDemoPage = () => {
  const [sortedItems, setSortedItems] = useState(items);
  const [rowLines, setRowLines] = useState(true);
  const [headerColor, setHeaderColor] = useState('#ccc');
  const [singleSelected, setSingleSelected] = useState<Item[]>([]);
  const [multiSelected, setMultiSelected] = useState<Item[]>([]);
  const [compactSelected, setCompactSelected] = useState<Item[]>([]);
  const [rowSelected, setRowSelected] = useState<Item[]>([]);

  const columns: UQDataGridColumn<Item>[] = [
    {
      columnId: 'id',
      renderHeaderCell: () => 'ID',
      renderCell: (item) => item.id,
      compare: (a, b) => a.id.localeCompare(b.id),
      minWidth: '140px',
    },
    {
      columnId: 'project',
      renderHeaderCell: () => 'Project',
      renderCell: (item) => item.project,
      compare: (a, b) => a.project.localeCompare(b.project),
      minWidth: '120px',
    },
    {
      columnId: 'customer',
      renderHeaderCell: () => 'Customer',
      renderCell: (item) => item.customer,
      compare: (a, b) => a.customer.localeCompare(b.customer),
    },
    {
      columnId: 'progress',
      renderHeaderCell: () => 'Progress',
      renderCell: (item) => (
        <span className={`progress-badge ${item.progress.status}`}>
          <UQIcon
            icon={
              item.progress.status === 'completed'
                ? 'check-contained'
                : item.progress.status === 'overdue'
                ? 'x-square-contained'
                : item.progress.status === 'warning'
                ? 'alert-circle'
                : 'clock-01'
            }
          />
          {item.progress.label}
        </span>
      ),
      compare: (a, b) => a.progress.label.localeCompare(b.progress.label),
      minWidth: '150px',
    },
    {
      columnId: 'date',
      renderHeaderCell: () => 'Date',
      renderCell: (item) => item.date,
      compare: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    },
    {
      columnId: 'person',
      renderHeaderCell: () => 'Person',
      renderCell: (item) => item.person,
      compare: (a, b) => a.person.localeCompare(b.person),
    },
    {
      columnId: 'edit',
      renderHeaderCell: () => 'Edit',
      renderCell: () => (
        <div className="icon-cell">
          <UQIcon icon="edit" />
        </div>
      ),
      minWidth: '80px',
    },
  ];

  const attributeColumns: UQDataGridColumn<typeof attributes[0]>[] = [
    {
      columnId: 'attribute',
      renderHeaderCell: () => 'Attribute',
      renderCell: (item) => item.attribute,
    },
    {
      columnId: 'type',
      renderHeaderCell: () => 'Type',
      renderCell: (item) => <code>{item.type}</code>,
    },
    {
      columnId: 'default',
      renderHeaderCell: () => 'Default',
      renderCell: (item) => <code>{item.default}</code>,
    },
    {
      columnId: 'description',
      renderHeaderCell: () => 'Description',
      renderCell: (item) => item.description,
    },
  ];

  const codeExample = `
import React, { useState } from 'react';
import UQDataGrid, { UQDataGridColumn } from '@uniqconsulting/ui';

const items = [
  { id: '1', project: 'Sharepoint', customer: 'Lorem', progress: { label: 'Completed', status: 'completed' }, date: '18.12.2024', person: 'Hans' },
];

const columns = [
  {
    columnId: 'id',
    renderHeaderCell: () => 'ID',
    renderCell: (item) => item.id,
    minWidth: '140px',
  },
  {
    columnId: 'project',
    renderHeaderCell: () => 'Project',
    renderCell: (item) => item.project,
    minWidth: '120px',
  },
];

const MyDataGrid = () => (
  <UQDataGrid
    items={items}
    columns={columns}
    sortable
    selectionMode="multiselect"
    getRowId={(item) => item.id}
    showCheckbox
    rowLines={true}
    headerColor="#1a237e"
    compactCheckboxCell
  />
);
`;

  return (
    <>
      <Breadcrumb crumbs={[BREADCRUMB_ROOT, 'Data Grid']} />
      <h1>UQDataGrid Demo</h1>
      <UQSpacer size="m" />

      {/* Description */}
      <h2>Description</h2>
      <p>
        The <code>UQDataGrid</code> component is a flexible and responsive data grid that supports:
        <ul>
          <li>Sorting (per column)</li>
          <li>Custom cell rendering</li>
          <li>Row selection (single, multiple, or none)</li>
          <li>Checkbox selection with optional compact style</li>
          <li>Row click selection (without checkboxes)</li>
          <li>Optional row lines and custom header color</li>
          <li>Per-column minimum width</li>
        </ul>
        It is designed to handle large datasets efficiently and provides a variety of customization options.
      </p>
      <UQSpacer size="m" />

      {/* Attributes Table */}
      <h2>Attributes</h2>
      <UQSpacer size="s" />
      <UQDataGrid
        items={attributes}
        columns={attributeColumns}
        sortable={false}
        selectionMode="none"
        rowLines
      />
      <UQSpacer size="m" />

      {/* Live Demo */}
      <h2>Live Demo</h2>
      <UQSpacer size="s" />
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <label>
          <input
            type="checkbox"
            checked={rowLines}
            onChange={() => setRowLines((v) => !v)}
            style={{ marginRight: 8 }}
          />
          Show row lines
        </label>
        <label>
          Header color:&nbsp;
          <input
            type="color"
            value={headerColor}
            onChange={e => setHeaderColor(e.target.value)}
            style={{ verticalAlign: 'middle' }}
          />
        </label>
      </div>
      <UQSpacer size="s" />
      <UQCard header="Data Grid Example (Multi-select)">
        <UQDataGrid
          items={sortedItems}
          columns={columns}
          sortable
          selectionMode="multiselect"
          getRowId={(item) => item.id}
          showCheckbox
          rowLines={rowLines}
          headerColor={headerColor}
          onRowSelect={setMultiSelected}
        />
        <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
          Selected IDs: {multiSelected.map(i => i.id).join(', ')}
        </div>
      </UQCard>
      <UQSpacer size="m" />

      <UQCard header="Data Grid Example (Single-select)">
        <UQDataGrid
          items={sortedItems}
          columns={columns}
          sortable
          selectionMode="single"
          getRowId={(item) => item.id}
          showCheckbox
          rowLines={rowLines}
          headerColor={headerColor}
          onRowSelect={setSingleSelected}
        />
        <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
          Selected ID: {singleSelected.map(i => i.id).join(', ')}
        </div>
      </UQCard>
      <UQSpacer size="m" />

      <UQCard header="Data Grid Example (Compact Checkbox)">
        <UQDataGrid
          items={sortedItems}
          columns={columns}
          sortable
          selectionMode="multiselect"
          getRowId={(item) => item.id}
          showCheckbox
          compactCheckboxCell
          rowLines={rowLines}
          headerColor={headerColor}
          onRowSelect={setCompactSelected}
        />
        <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
          Selected IDs: {compactSelected.map(i => i.id).join(', ')}
        </div>
      </UQCard>
      <UQSpacer size="m" />

      <UQCard header="Data Grid Example (Row Click Selection)">
        <UQDataGrid
          items={sortedItems}
          columns={columns}
          sortable
          selectionMode="single"
          getRowId={(item) => item.id}
          rowLines={rowLines}
          headerColor={headerColor}
          onRowSelect={setRowSelected}
        />
        <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
          Selected ID: {rowSelected.map(i => i.id).join(', ')}
        </div>
      </UQCard>
      <UQSpacer size="m" />

      <UQCard header="Data Grid Example (Empty)">
        <UQDataGrid
          items={[]} // empty array
          columns={columns}
          sortable
          selectionMode="multiselect"
          getRowId={(item) => item.id}
          showCheckbox
          rowLines={rowLines}
          headerColor={headerColor}
          emptyMessage="No data available."
        />
      </UQCard>
      <UQSpacer size="m" />

      {/* Code Example */}
      <h2>Code Example</h2>
      <UQCode code={codeExample} enableCopy />
    </>
  );
};

export default UQDataGridDemoPage;